import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Check, Folder, ImagePlus } from 'lucide-react';
import type { Asset, MediaQuery } from '@mg/shared';
import {
  POSTABLE_MIMES_LABEL,
  isPostableMime,
  MEDIA_MIXING_MESSAGE,
  MEDIA_PICKER_PAGE_SIZE,
} from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { useTenantKey } from '../../lib/useTenantKey';
import { cn } from '../../lib/cn';
import { Button } from '../../components/ui/Button';
import { Card, CardHeader } from '../../components/ui/Card';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { MediaThumb } from '../media/MediaThumb';
import { useMediaPages } from '../media/useMediaPages';
import { MediaSearchBox } from '../media/MediaSearchBox';
import { LoadMoreButton } from '../media/LoadMoreButton';
import { wouldMixMedia, type AttachedAsset, type MediaHeld } from './blocks';
import { assetName } from '../media/mediaDisplay';

/** `null` = the All view (every folder); otherwise the selected folder's id. */
type FolderFilter = string | null;

/**
 * What the picker is showing: everything it may offer, or one kind of it.
 *
 * Narrower than the Media Library page's own type filter on purpose — this panel already only
 * offers what a post can carry, so "Audio" and the design-tool formats would be chips that can
 * never match anything.
 */
type KindFilter = 'all' | 'image' | 'video';

const KIND_FILTERS: { id: KindFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'image', label: 'Images' },
  { id: 'video', label: 'Video' },
];

/**
 * Whether an asset belongs to the chosen kind. The split the chips make; the SERVER applies it now
 * (the chip is sent as the list's `type`), so the panel itself no longer calls this.
 */
export function matchesKind(mimeType: string, kind: KindFilter): boolean {
  if (kind === 'all') return true;
  return mimeType.toLowerCase().startsWith(`${kind}/`);
}

/** Base filter for the grid: every source, every type, nothing from the trash. */
const MEDIA_QUERY: MediaQuery = { source: 'all', type: 'all', deleted: false };

/**
 * Pick existing library images or video for the post, shown IN PLACE of the AI assist sidebar
 * rather than as a modal — the composer keeps its layout and the user keeps sight of the post.
 *
 * Selection is capped by `remaining` (the destination's image limit minus what the block already
 * holds — see `socialImageLimit`), tracked as an ordered list because pick order becomes gallery
 * order, and kept GLOBAL across folder changes so browsing never silently drops a pick.
 */
export function MediaLibraryPanel({
  remaining,
  onInsert,
  onClose,
  accept = isPostableMime,
  acceptLabel = POSTABLE_MIMES_LABEL,
  held,
  single = false,
  insertLabel = 'Insert Images',
  emptyTitle,
  insertDisabled = false,
}: {
  /** How many more images the post can take. 0 disables selection entirely. */
  remaining: number;
  /** Insert is held for now - nothing could land (a thread being sent takes no new media). */
  insertDisabled?: boolean;
  onInsert: (assets: AttachedAsset[]) => void;
  onClose: () => void;
  /**
   * Which files this particular picker may offer. Defaults to what a POST accepts.
   *
   * Overridable because the same panel serves two jobs with different rules: a post takes video, and
   * a design background cannot. Offering a file the caller will then refuse is the quiet mismatch
   * this exists to prevent — the design engine silently dropped any MP4 chosen here.
   */
  accept?: (mimeType: string | null | undefined) => boolean;
  /** Human form of `accept`, for the empty state and the footer note. */
  acceptLabel?: string;
  /**
   * What the post already holds, for a SOCIAL post — where photos and video may not be mixed. With
   * it, a selection that would mix them (on its own, or with what the post has) says so and cannot
   * be inserted. Absent → no such rule (a blog post, or a design background).
   */
  held?: MediaHeld;
  /**
   * Choose exactly ONE: picking another replaces the pick. For a slot that holds one file - a PDF
   * carousel - where the image allowance (`remaining`) says nothing.
   */
  single?: boolean;
  /** The Insert button's words, naming what goes in. */
  insertLabel?: string;
  /** What an empty grid is called, when it isn't "postable media". */
  emptyTitle?: string;
}) {
  const tenantKey = useTenantKey();
  const [folder, setFolder] = useState<FolderFilter>(null);
  const [kind, setKind] = useState<KindFilter>('all');
  /** The search in force, trimmed. It narrows the open folder ("All" searches everything). */
  const [search, setSearch] = useState('');
  /**
   * The picked ASSETS, not just their ids — selection is global across folders, so a pick made in
   * one folder must survive browsing to another. Resolving ids against the currently-loaded grid
   * would silently drop every pick that isn't in the folder you happen to be looking at when you
   * press Insert. Ordered, because pick order becomes gallery order.
   */
  const [selected, setSelected] = useState<AttachedAsset[]>([]);

  const foldersQuery = useQuery({
    queryKey: ['asset-folders', tenantKey],
    queryFn: ({ signal }) => api.listAssetFolders(signal),
    enabled: tenantKey !== '',
  });

  /**
   * Whether this picker can offer both kinds at all — asked of `accept`, which is the only thing
   * that knows. A post takes video; a design background does not.
   */
  const offersBothKinds = accept('image/png') && accept('video/mp4');
  /**
   * The list type to ask for while no chip narrows it, asked of `accept` the same way. A picker of
   * PDFs only asks for documents (`other`: not a picture, video or sound) and a picker of pictures
   * only asks for pictures. One that takes more than one of the three asks for everything - no
   * single type holds them all - and keeps what fits. Asking for everything in every case would
   * have a PDF picker say the library holds no PDFs whenever its newest pages hold none.
   */
  const takesPdf = accept('application/pdf');
  const baseType =
    takesPdf && !accept('image/png') && !accept('video/mp4')
      ? 'other'
      : accept('image/png') && !accept('video/mp4') && !takesPdf
        ? 'image'
        : 'all';

  // The library comes a page at a time, so the chip and the search go to the SERVER: a page then
  // comes back full of that kind, rather than a page of everything with a few of it.
  const query = useMemo<MediaQuery>(
    () => ({
      ...MEDIA_QUERY,
      type: kind !== 'all' ? kind : baseType,
      ...(folder ? { folderId: folder } : {}),
      ...(search ? { q: search } : {}),
    }),
    [baseType, folder, kind, search],
  );
  // What this picker may offer of a page. The server's `type` is a prefix, so this is still what
  // keeps audio and design documents out of a post: an EXACT allowlist, not an `image/` prefix -
  // that prefix also matches `image/svg+xml`, and an SVG cannot be published to any platform.
  // Generating/failed assets have no blob yet. While a page holds too few of these the hook reads
  // the next one on its own (a page that happens to be all SVG must not look like an empty library).
  const visible = useCallback((a: Asset) => accept(a.mimeType) && a.status === 'ready', [accept]);
  // Always re-read on open: an image uploaded from the composer moments ago would otherwise be
  // missing for the app-wide 30s staleTime. Cached pages still paint at once.
  const mediaPages = useMediaPages(query, MEDIA_PICKER_PAGE_SIZE, {
    visible,
    refetchOnMount: 'always',
  });
  const media = useMemo(() => mediaPages.assets.filter(visible), [mediaPages.assets, visible]);

  // `remaining` is LIVE: the gallery behind the panel still accepts drag-and-drop, and the
  // destination can change (LinkedIn 20 → X 4). Trim as the cap falls so the badges, the counter
  // and the footer all tell the truth — and so Insert can never be a button that does nothing.
  useEffect(() => {
    setSelected((prev) => {
      // Trim IMAGES to the new cap and keep every video — the cap says nothing about video.
      let allowance = remaining;
      const next = prev.filter((a) => !isImage(a) || allowance-- > 0);
      return next.length === prev.length ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining]);

  const selectedIds = useMemo(() => new Set(selected.map((a) => a.id)), [selected]);
  const count = selected.length;
  // `remaining` is the post's IMAGE allowance, so only images are charged against it. A video
  // becomes its own block and is not part of the gallery the cap describes — letting one consume a
  // slot would lock out an image the post could still take.
  const isImage = (a: { mimeType: string }) => a.mimeType.startsWith('image/');
  const imageCount = selected.filter(isImage).length;
  const mixes = held ? wouldMixMedia(held, selected) : false;
  const atImageCap = imageCount >= remaining;

  const toggle = (asset: Asset) =>
    setSelected((prev) => {
      if (prev.some((a) => a.id === asset.id)) return prev.filter((a) => a.id !== asset.id);
      const picked: AttachedAsset = {
        id: asset.id,
        blobUrl: asset.blobUrl,
        mimeType: asset.mimeType,
        posterUrl: asset.posterUrl,
        thumbUrl: asset.thumbUrl,
        name: asset.name,
        pageCount: asset.pageCount,
      };
      return single ? [picked] : [...prev, picked];
    });

  return (
    // At lg+ the panel is a working surface beside the post: sticky, and exactly as tall as the
    // modal's scrollport, so it stays in view while the user scrolls rather than sliding away.
    // `top-5` matches the scroll container's py-5, so the gap above it is identical at rest and
    // when stuck — and the height is the scrollport MINUS that padding, so it never overflows
    // either way. The var falls back for a composer rendered outside the modal, which sets none.
    // Below lg the grid is ONE column and this sits under the post, where pinning a full-height
    // pane would cover the thing it belongs to — so it's a plain block there.
    <Card className="flex h-[32rem] flex-col lg:sticky lg:top-5 lg:h-[var(--composer-pane-h,32rem)]">
      <CardHeader
        title="Media Library"
        // Only where there is something to choose BETWEEN. The same panel picks a design
        // background, where `accept` refuses video — a Video chip there could only ever come back
        // empty, and "All" would mean the one thing already shown.
        action={
          offersBothKinds ? (
            <div className="flex items-center gap-1.5" role="group" aria-label="Filter by type">
              {KIND_FILTERS.map((k) => (
                <button
                  key={k.id}
                  type="button"
                  aria-pressed={kind === k.id}
                  onClick={() => setKind(k.id)}
                  className={cn(
                    'focus-ring rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                    kind === k.id
                      ? 'border-ink-ghost bg-tint text-tint-fg'
                      : 'border-line-input text-ink-label hover:bg-hover',
                  )}
                >
                  {k.label}
                </button>
              ))}
            </div>
          ) : undefined
        }
      />
      <div className="shrink-0 border-b border-line-soft px-3 py-2">
        <MediaSearchBox value={search} onSearch={setSearch} />
      </div>
      {/* Not CardBody: its px-5/py-4 can't be overridden (cn joins, it doesn't tailwind-merge),
          and the grid has to run to the panel edges to scroll cleanly. */}
      <div className="flex min-h-0 flex-1 gap-3">
        {/*
          Folders are FLAT in the data model (AssetFolder has no parent), so this is a plain list —
          a read-only one, deliberately not MediaFolderSidebar: that component carries New folder /
          rename / delete / Trash, and destructive folder management has no place inside a picker.
        */}
        <nav
          aria-label="Folders"
          className="w-32 shrink-0 space-y-0.5 overflow-y-auto border-r border-line-soft py-3 pl-3"
        >
          <FolderRow label="All" selected={folder === null} onClick={() => setFolder(null)} />
          {(foldersQuery.data ?? []).map((f) => (
            <FolderRow
              key={f.id}
              label={f.name}
              selected={folder === f.id}
              onClick={() => setFolder(f.id)}
            />
          ))}
        </nav>

        <div className="min-h-0 flex-1 overflow-y-auto py-3 pr-3">
          {mediaPages.isLoading ? (
            <LoadingState label="Loading images…" />
          ) : mediaPages.isError ? (
            <ErrorState
              message={
                mediaPages.error instanceof ApiRequestError
                  ? mediaPages.error.message
                  : 'Could not load your media.'
              }
              onRetry={() => void mediaPages.refetch()}
            />
          ) : media.length === 0 ? (
            <>
            {search ? (
              <EmptyState
                title="No media matches your search"
                description="Try a different word, or clear the search."
              />
            ) : kind !== 'all' ? (
              // An empty grid because of the FILTER is a different situation from an empty library,
              // and saying "no postable media" here would send someone off to upload something they
              // already have. Name the filter, and offer the way back.
              <EmptyState
                title={kind === 'video' ? 'No video here' : 'No images here'}
                description={
                  folder
                    ? 'Nothing in this folder matches that filter.'
                    : 'Nothing in your library matches that filter.'
                }
                action={
                  <button
                    type="button"
                    onClick={() => setKind('all')}
                    className="focus-ring rounded text-sm font-medium text-link hover:underline"
                  >
                    Show all types
                  </button>
                }
              />
            ) : (
              <EmptyState
                title={
                  emptyTitle ??
                  (folder ? 'Nothing postable in this folder' : 'No postable media in your library')
                }
                description={`Only ${acceptLabel} files can be used here. Designs and templates stay in the library for editing.`}
                action={
                  <Link
                    to="/media"
                    className="focus-ring rounded text-sm font-medium text-link hover:underline"
                  >
                    Go to the Media Library
                  </Link>
                }
              />
            )}
            {/* The automatic reads stop after a few pages; a later one may still hold a usable file. */}
            {mediaPages.hasMore && (
              <LoadMoreButton
                size="sm"
                loading={mediaPages.isFetchingNextPage}
                failed={mediaPages.loadMoreFailed}
                onClick={mediaPages.loadMore}
              />
            )}
            </>
          ) : (
            <>
            <div role="group" aria-label="Library media" className="grid grid-cols-2 gap-2">
              {media.map((asset) => {
                const isSelected = selectedIds.has(asset.id);
                // At the cap only the already-selected tiles stay live, so a pick can be swapped —
                // and only IMAGE tiles lock, since video isn't what the cap is counting.
                const locked = !single && !isSelected && isImage(asset) && atImageCap;
                return (
                  <button
                    key={asset.id}
                    type="button"
                    aria-pressed={isSelected}
                    disabled={locked}
                    title={
                      locked
                        ? `That's all the images this post can take (${remaining}). Deselect one to swap it out.`
                        : assetName(asset)
                    }
                    onClick={() => toggle(asset)}
                    className={cn(
                      'focus-ring relative block overflow-hidden rounded-lg border-2',
                      isSelected ? 'border-action' : 'border-line hover:border-ink-ghost',
                      locked && 'cursor-not-allowed opacity-40',
                    )}
                  >
                    <div className="aspect-[4/3] w-full overflow-hidden bg-chip">
                      <MediaThumb asset={asset} className="h-full w-full" />
                    </div>
                    {isSelected && (
                      // Fixed graphite in both themes, like the other badges drawn ON a picture:
                      // it sits on the image, not on the page, so it does not follow the theme.
                      <span
                        className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#26332F] text-white"
                        aria-hidden
                      >
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {mediaPages.hasMore && (
              <div className="pt-3">
                <LoadMoreButton
                  size="sm"
                  loading={mediaPages.isFetchingNextPage}
                  failed={mediaPages.loadMoreFailed}
                  onClick={mediaPages.loadMore}
                />
              </div>
            )}
            </>
          )}
        </div>
      </div>

      {/* Its own full-width row, above the footer: squeezed into the footer's single flex row it
          crushed the count and clipped the Insert button beside it. */}
      {mixes && (
        <div
          role="alert"
          className="shrink-0 border-t border-amber-200 dark:border-warning-dark/30 bg-amber-50 dark:bg-warning-dark-tint px-3 py-2 text-xs leading-relaxed text-amber-800 dark:text-warning-dark"
        >
          {MEDIA_MIXING_MESSAGE}
        </div>
      )}
      {/* Docked footer: one action, whose label and weight follow the selection. */}
      <div className="flex shrink-0 items-center justify-between gap-3 border-t border-line-soft px-3 py-2.5">
        {/* Why something visible on the Media Library page may not be listed here. Said up front,
            rather than leaving someone hunting for an .svg design that can never go in a post. */}
        <span className="hidden text-xs text-ink-faint sm:block">{acceptLabel} only</span>
        <div className="flex items-center gap-3">
        {/*
          Shown for ANY selection, not just a full one.

          It used to appear only at the cap, which made it read as a warning rather than a running
          total — so while picking you had no idea how many you had or how many more you could add,
          and the number arrived exactly when it stopped being useful. The cap is still legible
          without it: tiles past the limit go inert and say so on hover.

          Matches the Generate modal's picker, which reports its count throughout.
        */}
        {count > 0 && !single && (
          <span className="text-xs text-ink-muted">
            {imageCount} of {remaining} images
            {count > imageCount &&
              ` · ${count - imageCount} video${count - imageCount === 1 ? '' : 's'}`}
          </span>
        )}
        {count === 0 ? (
          // Reached only when nothing is selected. It used to also fire when the post was full on
          // images, which is no longer the same thing: a post at its image cap can still take a
          // video, so Insert has to stay available.
          // "Done" implied something had been committed; this button only closes the panel, and its
          // sibling in the Generate modal says the same thing.
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close Media Library
          </Button>
        ) : (
          <Button
            size="sm"
            disabled={mixes || insertDisabled}
            onClick={() => {
              // Already within the cap (the trim effect above keeps it so); the helper clamps
              // once more against the live block list, which is the only count that can't be stale.
              onInsert(selected);
              onClose();
            }}
          >
            <ImagePlus className="h-4 w-4" aria-hidden />
            {insertLabel}
          </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

function FolderRow({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-current={selected ? 'true' : undefined}
      onClick={onClick}
      className={cn(
        'focus-ring flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm',
        selected ? 'bg-chip font-medium text-ink' : 'text-ink-label hover:bg-hover',
      )}
    >
      <Folder className="h-3.5 w-3.5 shrink-0 text-ink-faint" aria-hidden />
      <span className="truncate">{label}</span>
    </button>
  );
}
